<template>
  <div class="schedule-container">
    <div class="details">
      <div class="control">
        <label class="radio">
          <input
            type="radio"
            name="schedule-option"
            :value="ScheduleType.AnyTime"
            v-model="selectedSchedule"
            @change="switchedScheduleType"
          />
          At any time
        </label>
        <label class="radio">
          <input
            type="radio"
            name="schedule-option"
            :value="ScheduleType.InHours"
            v-model="selectedSchedule"
            @change="switchedScheduleType"
          />
          During business hours
        </label>
        <label class="radio">
          <input
            type="radio"
            name="schedule-option"
            :value="ScheduleType.OutOfHours"
            v-model="selectedSchedule"
            @change="switchedScheduleType"
          />
          Outside business hours
        </label>
        <label class="radio">
          <input
            type="radio"
            name="schedule-option"
            :value="ScheduleType.CustomTimes"
            v-model="selectedSchedule"
            @change="switchedScheduleType"
          />
          Custom
        </label>
      </div>
      <div class="online-times-picker" v-if="showCustomTimePicker">
        <BusinessHoursSelection
          v-model="customTimes"
          @input="scheduleChanged"
          :allowEmpty="false"
          @error="setInvalid"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BusinessHoursSelection from '../businessHoursSelection/BusinessHoursSelection.vue';
import { BusinessHoursOptions } from '../businessHoursSelection';
import { BusinessHoursViewModel } from '../../models';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { computed, PropType, ref, watch } from 'vue';

enum ScheduleType {
  AnyTime,
  InHours,
  OutOfHours,
  CustomTimes,
}

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
  },
  openingTimes: {
    type: Array as PropType<BusinessHoursViewModel[]>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'updated', condition: ConditionViewModel | undefined, customTimes: BusinessHoursViewModel[]): void;
}>();

const invalid = ref(false);
const selectedSchedule = ref(ScheduleType.AnyTime);
const customTimes = ref<BusinessHoursViewModel[]>([]);
const showCustomTimePicker = computed(() => selectedSchedule.value === ScheduleType.CustomTimes);

watch(
  () => props.openingTimes,
  (openingTimes: BusinessHoursViewModel[]) => (customTimes.value = openingTimes),
  { immediate: true },
);
watch(
  () => props.condition,
  () => (selectedSchedule.value = getSelectedSchedule()),
  { immediate: true },
);

function getSelectedSchedule() {
  if (hasNoSchedule()) {
    return ScheduleType.AnyTime;
  }

  if (isInHours()) {
    if (props.openingTimes.length) {
      return ScheduleType.CustomTimes;
    }
    return ScheduleType.InHours;
  }

  if (!isInHours()) {
    return ScheduleType.OutOfHours;
  }

  return ScheduleType.AnyTime;
}

function hasNoSchedule() {
  return !props.condition || !hasAttribute() || !isScheduleCondition();
}

function isInHours() {
  return (
    props.condition.attribute === 'bot.openingTimes' &&
    props.condition.operation === 'contains' &&
    props.condition.result
  );
}

function switchedScheduleType() {
  invalid.value = false;
  scheduleChanged();
}

function scheduleChanged() {
  if (selectedSchedule.value === ScheduleType.CustomTimes) {
    if (!customTimes.value.length) {
      customTimes.value = [BusinessHoursOptions.getDefaultModel()];
    }
  } else {
    customTimes.value = [];
  }
  emit('updated', createScheduleCondition(selectedSchedule.value), customTimes.value);
}

function setInvalid(value: boolean) {
  invalid.value = value;
}

function createScheduleCondition(type: ScheduleType) {
  switch (type) {
    case ScheduleType.InHours:
    case ScheduleType.CustomTimes:
      return createCondition(true);
    case ScheduleType.OutOfHours:
      return createCondition(false);
  }
}

function isScheduleCondition() {
  if (props.condition.attribute) {
    return props.condition.attribute === 'bot.openingTimes';
  }
  return false;
}

function hasAttribute() {
  return props.condition.attribute && props.condition.attribute.length > 0;
}

function createCondition(inHours: boolean) {
  return {
    attribute: 'bot.openingTimes',
    operation: 'contains',
    type: 'datetime',
    value: 'utcNow',
    result: inHours,
    invalid: invalid.value,
  } as ConditionViewModel;
}
</script>

<style scoped lang="scss">
.radio {
  display: block;
  margin: 0.25em;
}

.radio + .radio {
  margin-left: 0.25em;
}

.intro {
  margin-bottom: 1em;
}

.details {
  margin: 0 1em;
}

.online-times-picker {
  background-color: #f2f2f2;
  padding: 0.75em 1em 1em;
  margin-top: 1em;
}

:deep(.openingTimes-row .toggle-container) {
  margin: 0 0.5rem 0 0.25rem;
}
</style>
